import React, { useState, useEffect } from 'react';
import './css/Dashboard.css'; 
import './css/NewCustomerModal.css'; 
import Modal from './components/NewCustomerModal';
import ServerEvent from './components/ServerEvent';
import axios from "axios";
import TopBar from './components/TopBar';

// Main App Component
type PipelineColumnId = 'new' | 'create-papers' | 'invoice' | 'in-progress' | 'final-invoice';

type Task = {
    id: string;
    clientName: string;
    travelDestination: string;
    dueDate: string;
    taskDescription: string;
};

type Status = {
    code : string
}

type Lead = {
    id: number,
    name: string,
    travelDestination: string,
    currentLeadStatus: Status,
}

const lead_ep = process.env.REACT_APP_BE_API_BASE_URL + "/api/leads";

const Dashboard = () => {

    const [leads,setLeads] = useState<Lead[]>([]);

    //Load post after the DOM has been updated
    useEffect(() => {
        axios.get(lead_ep, {
            headers: {
                'Authorization': process.env.REACT_APP_BE_API_BASIC_AUTH || '',
            }
        }).then((data) => {
            //console.log(`Lead data= ${JSON.stringify(data.data,null,2)}`);
            //setLeads(data.data as Lead[]);
            loadPipelineTasks(data.data as Lead[]);
            
        });
        
    }, []);

    
    const loadPipelineTasks = (leads : Lead[]) => {
        setPipelineTasks(prevTasks => {
                // Initialize pipelineTasks with the fetched leads
                const newPipelineTasks: Record<PipelineColumnId, Task[]> = {
                    'new': [],
                    'create-papers': [],
                    'invoice': [],
                    'in-progress': [],
                    'final-invoice': []
                };

                (leads).forEach((lead: Lead) => {

                    let leadDescription = `Initial consultation for '${lead.name}'`;
                    if (lead.travelDestination) {
                        leadDescription = `Plan trip for '${lead.name}' to ${lead.travelDestination}`;
                    }

                    const task: Task = {
                        id: `card-${lead.id}`,
                        clientName: lead.name,
                        travelDestination: lead.travelDestination,
                        dueDate: 'N/A', // Assuming no due date for leads
                        taskDescription: leadDescription
                    };
                    const columnId = lead.currentLeadStatus.code as PipelineColumnId;
                    if (newPipelineTasks[columnId]) {
                        newPipelineTasks[columnId].push(task);
                    }
                });

                return newPipelineTasks;
            });
    }




    // State to manage the tasks in each pipeline column
    const [pipelineTasks, setPipelineTasks] = useState<Record<PipelineColumnId, Task[]>>({
        // 'new': [
        //     { id: 'card-1', clientName: 'The Millers', travelDestination: 'Unknown', dueDate: '2025-07-05', taskDescription: "Initial consultation for 'The Millers'" },
        //     { id: 'card-2', clientName: 'Japan Trip', travelDestination: 'Japan', dueDate: '2025-07-10', taskDescription: "Follow-up on 'Japan Trip' inquiry" }
        // ],
        // 'create-papers': [
        //     { id: 'card-3', clientName: 'Smith Family', travelDestination: 'Cruise', dueDate: '2025-07-08', taskDescription: "Draft itinerary for 'Smith Family Cruise'" }
        // ],
        // 'invoice': [],
        // 'in-progress': [
        //     { id: 'card-4', clientName: 'Garcia Honeymoon', travelDestination: 'Unknown', dueDate: '2025-07-15', taskDescription: "Book flights for 'Garcia Honeymoon'" }
        // ],
        // 'final-invoice': []

        'new': [],
        'create-papers': [],
        'invoice': [],
        'in-progress': [],
        'final-invoice': []
    });



    // State for managing the modal visibility
    const [isModalOpen, setIsModalOpen] = useState(false);
    // State for form inputs in the modal
    const [clientName, setClientName] = useState('Client');
    const [travelDestination, setTravelDestination] = useState('');
    const [taskDueDate, setTaskDueDate] = useState('');

    // Effect to update column counts whenever pipelineTasks changes
    useEffect(() => {
        // This effect will run after initial render and whenever pipelineTasks state updates.
        // The counts are dynamically calculated directly in the JSX using pipelineTasks.
        // No explicit DOM manipulation needed here.
    }, [pipelineTasks]);

    /**
     * Handles the start of a drag operation.
     * Sets the dataTransfer object with the ID of the dragged task card.
     */
    const handleDragStart = (event: React.DragEvent, cardId: string) => {
        event.dataTransfer.setData('text/plain', cardId);
    };

    /**
     * Allows an element to be dropped into a drop zone.
     * Prevents the default browser behavior for dragover event.
     * @param {React.DragEvent} event The drag event.
     */
    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
    };

    /**
     * Handles the drag enter event for a column, adding a visual highlight.
     * @param {React.DragEvent} event The drag event.
     */
    const handleDragEnter = (event: React.DragEvent) => {
        if (event.currentTarget.classList.contains('column-drop-zone') && event.dataTransfer.types.includes('text/plain')) {
            event.currentTarget.classList.add('drag-over');
        }
    };

    /**
     * Handles the drag leave event for a column, removing the visual highlight.
     * @param {React.DragEvent} event The drag event.
     */
    const handleDragLeave = (event: React.DragEvent) => {
        if (event.currentTarget.classList.contains('column-drop-zone')) {
            event.currentTarget.classList.remove('drag-over');
        }
    };

    /**
     * Handles the drop event, moving a task card between columns.
     * @param {React.DragEvent} event The drop event.
     * @param {PipelineColumnId} targetColumnId The ID of the column where the card is dropped.
     */
    const handleDrop = (event: React.DragEvent, targetColumnId: PipelineColumnId) => {
        event.preventDefault();
        event.currentTarget.classList.remove('drag-over');
        const cardId = event.dataTransfer.getData('text/plain');
        if (!cardId) return;

        let sourceColumnId: PipelineColumnId | null = null;
        let draggedTask: Task | null = null;

        // Find the source column and the dragged task
        (Object.keys(pipelineTasks) as PipelineColumnId[]).forEach(columnId => {
            const taskIndex = pipelineTasks[columnId].findIndex(task => task.id === cardId);
            if (taskIndex !== -1) {
                sourceColumnId = columnId;
                draggedTask = pipelineTasks[columnId][taskIndex];
            }
        });

        if (sourceColumnId && draggedTask && sourceColumnId !== targetColumnId) {
            // Create new state for tasks to ensure immutability
            const newPipelineTasks: Record<PipelineColumnId, Task[]> = { ...pipelineTasks };

            // Remove from source column
            newPipelineTasks[sourceColumnId as PipelineColumnId] = newPipelineTasks[sourceColumnId as PipelineColumnId].filter(
                (task: Task) => task.id !== cardId
            );

            // Add to target column
            newPipelineTasks[targetColumnId] = [
                ...newPipelineTasks[targetColumnId],
                draggedTask
            ];

            setPipelineTasks(newPipelineTasks); // Update the state
            updateLead(Number(cardId.split('-')[1]), targetColumnId); // Update lead status via API
        }
    };

    const updateLead = async (leadId: number, newStatus: string) => {
        try {
            const response = await axios.patch(`${lead_ep}/${leadId}`, {
                currentStatus: {
                    code: newStatus
                }
            }, {
                headers: {
                    'Authorization': process.env.REACT_APP_BE_API_BASIC_AUTH || '',
                }
            });
            console.log(`Lead updated successfully: ${JSON.stringify(response.data)}`);
        }catch (error){

        }
    }

    /**
     * Adds a new travel client task card to the "New" column.
     */
    const addNewClientTask = () => {
        if (clientName.trim() === "") {
            console.error("Client name cannot be empty.");
            // In a real application, display a user-friendly message
            return;
        }

        // Generate a unique ID for the new card
        const newCardId = `card-${crypto.randomUUID()}`;
        let taskDescription = `Initial consultation for '${clientName}'`;
        if (travelDestination) {
            taskDescription = `Plan trip for '${clientName}' to ${travelDestination}`;
        }

        const newClient = {
            id: newCardId,
            clientName: clientName.trim(),
            travelDestination: travelDestination.trim(),
            dueDate: taskDueDate || 'N/A',
            taskDescription: taskDescription
        };

        setPipelineTasks(prevTasks => ({
            ...prevTasks,
            'new': [...prevTasks['new'], newClient]
        }));

        // Clear form inputs and close modal
        setClientName('');
        setTravelDestination('');
        setTaskDueDate('');
        setIsModalOpen(false);
    };

    const addNewLeadViaWebhook = async (name: string) => {
        //setClientName('New Lead AAAAA');
        //setTravelDestination('China');

        console.log(`clientName in dashboard: ${name}`);


        // Generate a unique ID for the new card
        const newCardId = `card-${crypto.randomUUID()}`;
        let taskDescription = `Initial consultation for '${name}'`;
        if (travelDestination) {
            taskDescription = `Plan trip for '${name}' to ${travelDestination}`;
        }

        const newClient = {
            id: newCardId,
            clientName: name.trim(),
            travelDestination: travelDestination.trim(),
            dueDate: taskDueDate || 'N/A',
            taskDescription: taskDescription
        };

        setPipelineTasks(prevTasks => ({
            ...prevTasks,
            'new': [...prevTasks['new'], newClient]
        }));

    };   
    


    // --- Helper Components (defined within App for simplicity in single file) ---

    /**
     * Renders a single draggable task card.
     * @param {object} props - Component props.
     * @param {string} props.id - Unique ID of the card.
     * @param {string} props.taskDescription - Description of the task.
     * @param {string} props.dueDate - Due date for the task.
     * @param {function(React.DragEvent, string): void} props.onDragStart - Drag start handler.
     * @param {string} props.bgColorClass - Tailwind background color class.
     * @param {string} props.textColorClass - Tailwind text color class.
     * @param {string} props.borderColorClass - Tailwind border color class.
     */
    type TaskCardProps = {
        id: string;
        taskDescription: string;
        dueDate: string;
        onDragStart: (e: React.DragEvent, id: string) => void;
        bgColorClass: string;
        textColorClass: string;
        borderColorClass: string;
    };

    const TaskCard: React.FC<TaskCardProps> = ({ id, taskDescription, dueDate, onDragStart, bgColorClass, textColorClass, borderColorClass }) => (
        <div
            id={id}
            className={`task-card ${bgColorClass} p-3 rounded-lg shadow-sm text-sm ${textColorClass} border ${borderColorClass}`}
            draggable="true"
            onDragStart={(e) => onDragStart(e, id)}
        >
            <p className="font-medium">Task: {taskDescription}</p>
            <p className="text-xs mt-1">Due: {dueDate}</p>
        </div>
    );

    /**
     * Renders a pipeline column.
     * @param {object} props - Component props.
     * @param {string} props.id - Unique ID of the column.
     * @param {string} props.title - Title of the column.
     * @param {string} props.borderColorClass - Tailwind border color class for the title.
     * @param {Array<object>} props.tasks - Array of task objects in this column.
     * @param {function(React.DragEvent): void} props.onDragOver - Drag over handler.
     * @param {function(React.DragEvent): void} props.onDragEnter - Drag enter handler.
     * @param {function(React.DragEvent): void} props.onDragLeave - Drag leave handler.
     * @param {function(React.DragEvent, string): void} props.onDrop - Drop handler.
     */
    type ColumnProps = {
        id: PipelineColumnId;
        title: string;
        borderColorClass: string;
        tasks: Task[];
        onDragOver: (event: React.DragEvent) => void;
        onDragEnter: (event: React.DragEvent) => void;
        onDragLeave: (event: React.DragEvent) => void;
        onDrop: (event: React.DragEvent, id: PipelineColumnId) => void;
    };

    const Column: React.FC<ColumnProps> = ({ id, title, borderColorClass, tasks, onDragOver, onDragEnter, onDragLeave, onDrop }) => {
        // Determine card colors based on column ID or a default
        const getCardColorClasses = (columnId: PipelineColumnId) => {
            switch (columnId) {
                case 'new': return { bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200' };
                case 'create-papers': return { bgColor: 'bg-purple-100', textColor: 'text-purple-800', borderColor: 'border-purple-200' };
                case 'invoice': return { bgColor: 'bg-green-100', textColor: 'text-green-800', borderColor: 'border-green-200' };
                case 'in-progress': return { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', borderColor: 'border-yellow-200' };
                case 'final-invoice': return { bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-200' };
                default: return { bgColor: 'bg-gray-100', textColor: 'text-gray-800', borderColor: 'border-gray-200' };
            }
        };

        return (
            <div className="bg-white rounded-lg shadow-xl p-4 w-full sm:w-1/5 flex-shrink-0">
                <h2 className={`text-xl font-semibold text-gray-700 mb-4 pb-2 border-b-2 ${borderColorClass}`}>
                    {title} (<span id={`count-${id}`}>{tasks.length}</span>)
                </h2>
                <div
                    id={`column-${id}`}
                    className="column-drop-zone p-3 rounded-lg space-y-3"
                    onDrop={(e) => onDrop(e, id)}
                    onDragOver={onDragOver}
                    onDragEnter={onDragEnter}
                    onDragLeave={handleDragLeave}
                >
                    {tasks.map(task => {
                        console.log(`Tasks in column ${id}: ${JSON.stringify(tasks)}`)
                        const { bgColor, textColor, borderColor } = getCardColorClasses(id);
                        return (
                            <TaskCard
                                key={task.id}
                                id={task.id}
                                // id={`card-${crypto.randomUUID()}`}
                                // id={'card-3'}
                                taskDescription={task.taskDescription}
                                dueDate={task.dueDate}
                                onDragStart={handleDragStart}
                                bgColorClass={bgColor}
                                textColorClass={textColor}
                                borderColorClass={borderColor}
                            />
                        );

                        
                    })}

                    {/* {leads.map(lead => {

                        console.log(`Lead in column ${id}: ${JSON.stringify(lead)}`);
                        const { bgColor, textColor, borderColor } = getCardColorClasses(id);

                        let leadDescription = `Initial consultation for '${lead.name}'`;
                        if (lead.travelDestination) {
                            leadDescription = `Plan trip for '${lead.name}' to ${lead.travelDestination}`;
                        }

                        return (
                            <>
                            {( id===lead.currentLeadStatus.code &&

                                <>
                                    <TaskCard 
                                        key={lead.id}
                                        id={'card-'+lead.id}
                                        taskDescription={leadDescription}
                                        dueDate={'N/A'}
                                        onDragStart={handleDragStart}
                                        bgColorClass={bgColor}
                                        textColorClass={textColor}
                                        borderColorClass={borderColor}
                                    />
                                </>

                            )}
                            </>
                        );
                    })} */}
                </div>
            </div>
        );
    };

 

    return (
        <>  
        <TopBar
            onNavigate={() => {window.location.replace('/')}}
            currentPage="Dashboard"
        />
        <div className='dashboard-continer' style={{paddingLeft: '40px', paddingRight: '40px', paddingTop: '70px'}}>
        <div className="container mx-auto p-4 flex flex-wrap justify-center items-start container_adjust">
            <div className="w-full flex justify-between items-center mb-8">
                <h3 className="text-4xl font-bold text-gray-800 rounded-lg py-4 px-6 bg-white shadow-lg">
                    Leads Dashboard
                </h3>
                {/* Add New Client Button */}
                <button
                    id="add-customer-btn"
                    onClick={() => setIsModalOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                >
                    Add New Lead
                </button>
            </div>

            {/* Dashboard Columns Container */}
            <div className="flex flex-col sm:flex-row gap-6 w-full justify-center items-start">
                <Column
                    id="new"
                    title="New"
                    borderColorClass="border-blue-500"
                    tasks={pipelineTasks.new}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                />
                <Column
                    id="create-papers"
                    title="Create Documents"
                    borderColorClass="border-purple-500"
                    tasks={pipelineTasks['create-papers']}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                />
                <Column
                    id="invoice"
                    title="Invoice"
                    borderColorClass="border-green-500"
                    tasks={pipelineTasks.invoice}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                />
                <Column
                    id="in-progress"
                    title="In Progress"
                    borderColorClass="border-yellow-500"
                    tasks={pipelineTasks['in-progress']}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                />
                <Column
                    id="final-invoice"
                    title="Finalized"
                    borderColorClass="border-red-500"
                    tasks={pipelineTasks['final-invoice']}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                />
            </div>

            {/* Modal for adding new clients */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                clientName={clientName}
                onClientNameChange={(e) => setClientName(e.target.value)}
                travelDestination={travelDestination}
                onTravelDestinationChange={(e) => setTravelDestination(e.target.value)}
                taskDueDate={taskDueDate}
                onTaskDueDateChange={(e) => setTaskDueDate(e.target.value)}
                onAddClientTask={addNewClientTask}
            />

            <ServerEvent setClientName={setClientName} addNewLeadViaWebhook={addNewLeadViaWebhook}/>
        </div>
        </div>
        </>
    );
};

export default Dashboard;
